import { Component, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { RolesService } from "../../services/api/roles/roles.service";
import { PermissionService } from "../../services/api/permission/permission.service";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { TableModule } from "@openng/optimus-ui/table";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CheckboxModule } from "@openng/optimus-ui/checkbox";
import { FormsModule } from "@angular/forms";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { RoleComponent } from "./forms/role/role.component";
import { first } from "rxjs";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { SimplebarAngularModule } from "simplebar-angular";
import { Role } from "../../models/api/role";

@Component({
  selector: "app-role-management",
  standalone: true,
  imports: [
    HasPermissionDirective,
    SvgIconComponent,
    TableModule,
    TranslateModule,
    CheckboxModule,
    FormsModule,
    TimeoutComponent,
    ProgressSpinnerModule,
    SimplebarAngularModule,
  ],
  templateUrl: "./role-management.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./role-management.component.scss",
})
export class RoleManagementComponent implements OnInit {
  public permissions = inject(PermissionService);
  private roleService = inject(RolesService);
  private translate = inject(TranslateService);

  private dialog = inject(DialogService);
  private ref: DynamicDialogRef | null = null;

  public roles: WritableSignal<Role[]> = signal([]);

  public loading = signal(true);
  public timeOut = createTimeoutSignal(this.loading);

  public ngOnInit() {
    this.getRoles();
  }

  /**
   * Loads all roles from the server and updates the roles signal.
   * @remark This method is called when the component is initialized.
   */
  private getRoles() {
    this.loading.set(true);
    this.roleService
      .getRoles()
      .pipe(first())
      .subscribe({
        next: (roles) => {
          this.roles.set(roles);
          this.loading.set(false);
        },
      });
  }

  /**
   * Initiates the editing process for a given role.
   * Fetches the role details from the server using its ID and opens the role dialog in edit mode.
   * @param role - The role object that needs to be edited.
   */
  public onRoleEdit(role: Role) {
    if (role.id)
      this.roleService
        .getRoleById(role.id)
        .pipe(first())
        .subscribe({
          next: (role) => {
            this.openRoleDialog(role, "edit");
          },
        });
  }

  /**
   * Opens the role dialog in add mode.
   * Creates a new role object and opens the role dialog in add mode.
   */
  public onRoleAdd() {
    const emptyRole = new Role();
    this.openRoleDialog(emptyRole, "add");
  }

  /**
   * Opens the role dialog in the given action type.
   * @param role - The role object to be edited or added.
   * @param actionType - The action type, either "add" or "edit".
   */
  private openRoleDialog(role: Role, actionType: "add" | "edit") {
    this.ref = this.dialog.open<RoleComponent>(RoleComponent, {
      data: {
        role: role,
        actionType: actionType,
      },
      header: this.translate.instant("role_management.form.title." + actionType),
      modal: true,
      width: "80vw",
      contentStyle: { overflow: "auto", padding: "20px 100px" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      closable: true,
    });
    this.ref?.onClose.subscribe((success: boolean) => {
      if (success) {
        this.getRoles();
      }
    });
  }
}
